import React, { useState, useRef, useEffect } from 'react';
import { Settings as SettingsIcon, Moon, Sun, Palette, Lock, Eye, EyeOff, Check, X, User } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = window.location.hostname.includes('preview.emergentagent.com')
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const AVATARS = [
  { id: 'avatar_1', label: 'Kitty' },
  { id: 'avatar_2', label: 'Star' },
  { id: 'avatar_3', label: 'Flower' },
  { id: 'avatar_4', label: 'Bear' },
  { id: 'avatar_5', label: 'Beret' },
  { id: 'avatar_6', label: 'Bunny' },
  { id: 'avatar_7', label: 'Glasses' },
  { id: 'avatar_8', label: 'Music' },
  { id: 'avatar_9', label: 'Sailor' },
  { id: 'avatar_10', label: 'Plant' },
  { id: 'avatar_11', label: 'Neko' },
  { id: 'avatar_12', label: 'Painter' },
  { id: 'avatar_13', label: 'Dreamy' },
  { id: 'avatar_14', label: 'Panda' },
  { id: 'avatar_15', label: 'Sunny' },
  { id: 'avatar_16', label: 'Skater' },
];

export default function SettingsGear() {
  const [open, setOpen] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showUsernameForm, setShowUsernameForm] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwStatus, setPwStatus] = useState(null);
  const [pwError, setPwError] = useState('');
  const [newName, setNewName] = useState('');
  const [nameStatus, setNameStatus] = useState(null); // null | 'saving' | 'success' | 'error'
  const [nameError, setNameError] = useState('');
  const [avatarSaving, setAvatarSaving] = useState(false);
  const { darkMode, toggleDarkMode, palette, setPalette, PALETTES } = useTheme();
  const { t } = useLanguage();
  const { user, updateUser } = useAuth();
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const resetPwForm = () => {
    setCurrentPw('');
    setNewPw('');
    setConfirmPw('');
    setShowCurrent(false);
    setShowNew(false);
    setPwStatus(null);
    setPwError('');
    setShowPasswordForm(false);
  };

  const resetNameForm = () => {
    setNewName('');
    setNameStatus(null);
    setNameError('');
    setShowUsernameForm(false);
  };

  const handleChangeUsername = async () => {
    setNameError('');
    const trimmed = newName.trim();
    if (!trimmed || trimmed.length > 30) {
      setNameError('Name must be 1-30 characters');
      return;
    }
    setNameStatus('saving');
    try {
      const res = await axios.post(`${API_URL}/api/auth/change-username`, {
        new_name: trimmed,
      }, { withCredentials: true });
      updateUser({ name: res.data.name });
      setNameStatus('success');
      setTimeout(resetNameForm, 1500);
    } catch (err) {
      setNameStatus('error');
      setNameError(err.response?.data?.detail || 'Failed to change username');
    }
  };

  const handleChangeAvatar = async (avatarId) => {
    if (avatarSaving || user?.avatar === avatarId) return;
    setAvatarSaving(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-avatar`, {
        avatar_id: avatarId,
      }, { withCredentials: true });
      updateUser({ avatar: avatarId });
    } catch (err) {
      console.error('Failed to change avatar:', err);
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (newPw.length < 6) {
      setPwError('Must be at least 6 characters');
      return;
    }
    if (newPw !== confirmPw) {
      setPwError("Passwords don't match");
      return;
    }
    setPwStatus('saving');
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: currentPw,
        new_password: newPw,
      }, { withCredentials: true });
      setPwStatus('success');
      setTimeout(resetPwForm, 1500);
    } catch (err) {
      setPwStatus('error');
      setPwError(err.response?.data?.detail || 'Failed to change password');
    }
  };

  return (
    <div className="fixed top-3 left-3 z-[60]" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-2 rounded-full border-[1.5px] border-[var(--border-soft)] shadow-sm bg-white hover:bg-[var(--c3)] text-[#1A1A1A] transition-all"
        data-testid="settings-gear"
      >
        <SettingsIcon className="w-5 h-5" />
      </button>

      {open && (
        <div className="absolute top-12 left-0 w-72 max-h-[80vh] overflow-y-auto bg-white rounded-2xl border-[1.5px] border-[var(--border-soft)] shadow-lg" data-testid="settings-panel">
          {/* Header */}
          <div className="px-4 py-3 border-b-[1.5px] border-[var(--border-soft)]">
            <p className="font-heading font-bold text-lg text-[#1A1A1A]">{t('settings')}</p>
          </div>

          {/* Appearance */}
          <div className="px-4 py-3 border-b-[1.5px] border-[var(--border-soft)]">
            <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">{t('appearance')}</p>
            <div className="flex gap-2">
              <button
                onClick={() => { if (darkMode) toggleDarkMode(); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-bold transition-all ${
                  !darkMode
                    ? 'bg-[var(--c1)] text-[#1A1A1A]'
                    : 'bg-transparent text-[#9CA3AF] hover:text-[#1A1A1A]'
                }`}
                data-testid="settings-light-mode"
              >
                <Sun className="w-4 h-4" /> {t('lightMode')}
              </button>
              <button
                onClick={() => { if (!darkMode) toggleDarkMode(); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-bold transition-all ${
                  darkMode
                    ? 'bg-[var(--c3)] text-[#1A1A1A]'
                    : 'bg-transparent text-[#9CA3AF] hover:text-[#1A1A1A]'
                }`}
                data-testid="settings-dark-mode"
              >
                <Moon className="w-4 h-4" /> {t('darkMode')}
              </button>
            </div>
          </div>

          {/* Color Theme */}
          <div className="px-4 py-3 border-b-[1.5px] border-[var(--border-soft)]">
            <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2 flex items-center gap-1">
              <Palette className="w-3 h-3" /> {t('colorTheme')}
            </p>
            <div className="flex flex-col gap-1">
              {PALETTES.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPalette(p.id)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    palette === p.id
                      ? 'bg-[var(--c2)] text-[#1A1A1A] font-bold'
                      : 'text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#1A1A1A]'
                  }`}
                  data-testid={`settings-palette-${p.id}`}
                >
                  <div className="flex gap-1">
                    {p.colors.map((color, i) => (
                      <div
                        key={i}
                        className="w-5 h-5 rounded-full border border-black/10"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <span>{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Avatar Picker */}
          <div className="px-4 py-3 border-b-[1.5px] border-[var(--border-soft)]">
            <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2 flex items-center gap-1">
              <User className="w-3 h-3" /> Avatar
            </p>
            <div className="grid grid-cols-4 gap-2">
              {AVATARS.map((av) => (
                <button
                  key={av.id}
                  onClick={() => handleChangeAvatar(av.id)}
                  disabled={avatarSaving}
                  className={`relative aspect-square rounded-xl border-[2px] overflow-hidden transition-all hover:scale-105 ${
                    user?.avatar === av.id
                      ? 'border-[var(--c3)] ring-2 ring-[var(--c3)] ring-offset-1 shadow-md'
                      : 'border-[var(--border-soft)] hover:border-[var(--c2)]'
                  }`}
                  data-testid={`avatar-option-${av.id}`}
                >
                  <img src={`/avatars/${av.id}.png`} alt={av.label} className="w-full h-full object-cover" />
                  {user?.avatar === av.id && (
                    <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-[var(--c3)] flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Change Username */}
          <div className="px-4 py-3 border-b-[1.5px] border-[var(--border-soft)]">
            {!showUsernameForm ? (
              <button
                onClick={() => { setShowUsernameForm(true); setNewName(user?.name || ''); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#1A1A1A] transition-all"
                data-testid="settings-change-username-btn"
              >
                <User className="w-4 h-4" /> Change Username
              </button>
            ) : (
              <div className="space-y-2.5" data-testid="change-username-form">
                <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide flex items-center gap-1">
                  <User className="w-3 h-3" /> Change Username
                </p>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="New display name"
                  maxLength={30}
                  className="w-full px-3 py-2 text-sm rounded-xl border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:outline-none transition-all"
                  data-testid="new-username-input"
                />
                <p className="text-xs text-[#9CA3AF] px-1">{newName.trim().length}/30 characters</p>
                {nameError && (
                  <p className="text-xs text-red-500 font-medium px-1" data-testid="username-error">{nameError}</p>
                )}
                {nameStatus === 'success' && (
                  <p className="text-xs text-emerald-600 font-medium px-1 flex items-center gap-1" data-testid="username-success">
                    <Check className="w-3 h-3" /> Username changed!
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={resetNameForm}
                    className="flex-1 py-2 rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#F3F4F6] transition-all"
                    data-testid="cancel-username-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangeUsername}
                    disabled={!newName.trim() || nameStatus === 'saving'}
                    className="flex-1 py-2 rounded-xl text-sm font-bold bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A] disabled:opacity-40 transition-all"
                    data-testid="save-username-btn"
                  >
                    {nameStatus === 'saving' ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Change Password */}
          <div className="px-4 py-3">
            {!showPasswordForm ? (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#F9FAFB] hover:text-[#1A1A1A] transition-all"
                data-testid="settings-change-password-btn"
              >
                <Lock className="w-4 h-4" /> Change Password
              </button>
            ) : (
              <div className="space-y-2.5" data-testid="change-password-form">
                <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Change Password
                </p>

                {/* Current Password */}
                <div className="relative">
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    placeholder="Current password"
                    className="w-full px-3 py-2 pr-9 text-sm rounded-xl border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:outline-none transition-all"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4A5568]"
                  >
                    {showCurrent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>

                {/* New Password */}
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    placeholder="New password (6+ chars)"
                    className="w-full px-3 py-2 pr-9 text-sm rounded-xl border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:outline-none transition-all"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4A5568]"
                  >
                    {showNew ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>

                {/* Confirm New Password */}
                <input
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full px-3 py-2 text-sm rounded-xl border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:outline-none transition-all"
                  data-testid="confirm-password-input"
                />

                {/* Error message */}
                {pwError && (
                  <p className="text-xs text-red-500 font-medium px-1" data-testid="password-error">{pwError}</p>
                )}

                {/* Success message */}
                {pwStatus === 'success' && (
                  <p className="text-xs text-emerald-600 font-medium px-1 flex items-center gap-1" data-testid="password-success">
                    <Check className="w-3 h-3" /> Password changed!
                  </p>
                )}

                {/* Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={resetPwForm}
                    className="flex-1 py-2 rounded-xl text-sm font-medium text-[#6B7280] hover:bg-[#F3F4F6] transition-all"
                    data-testid="cancel-password-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    disabled={!currentPw || !newPw || !confirmPw || pwStatus === 'saving'}
                    className="flex-1 py-2 rounded-xl text-sm font-bold bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A] disabled:opacity-40 transition-all"
                    data-testid="save-password-btn"
                  >
                    {pwStatus === 'saving' ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
